import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Typography, Alert, Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import FileUploader from '../components/FileUploader';
import WorkflowForm from '../components/WorkflowForm';
import JobDashboard from '../components/JobDashboard';
import UnifiedJobQueue from '../components/UnifiedJobQueue';
import WorkflowHelp from '../components/WorkflowHelp';

export default function WorkflowPage() {
  const { practice, workflow } = useParams();
  const [files, setFiles] = useState<File[]>([]);

  // Get workflow details and recipe requirements
  const { data: workflowData, isLoading } = useQuery({
    queryKey: ['workflow-details', practice, workflow],
    queryFn: async () => {
      if (!practice || !workflow) return null;
      const [practices, recipeReqs] = await Promise.all([
        apiService.getPracticeDetails(''),
        apiService.getRecipeRequirements(practice, workflow)
      ]);
      const practiceData = practices.find((p: any) => p.id === practice);
      const workflowData = practiceData?.workflows.find((w: any) => w.id === workflow);
      return workflowData ? { 
        practice: practiceData, 
        workflow: workflowData,
        recipeRequirements: recipeReqs
      } : null;
    },
    enabled: !!(practice && workflow)
  });

  const workflowDescription = useMemo(() => {
    const recipe = workflowData?.recipeRequirements;
    if (!recipe) {
      return undefined;
    }

    return (
      recipe.description ||
      recipe?.recipe?.user_interface?.description ||
      recipe?.recipe?.metadata?.description ||
      recipe?.recipe?.metadata?.comment ||
      undefined
    );
  }, [workflowData]);

  // Extract step workflow IDs from unified_workflow config (if this is a multi-step workflow)
  const stepWorkflows = useMemo(() => {
    const unifiedConfig = workflowData?.workflow?.unified_workflow;
    if (unifiedConfig?.enabled && unifiedConfig?.steps) {
      return unifiedConfig.steps.map((step: any) => step.workflow);
    }
    return [];
  }, [workflowData]);

  // If no specific workflow, show main dashboard
  if (!practice || !workflow) {
    return <JobDashboard />;
  }

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (!workflowData) {
    return (
      <Alert severity="error">
        Workflow not found: {practice}/{workflow}
      </Alert>
    );
  }

  const formatTitle = (str: string) => 
    str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  // Get the best display name: ui-schema > workflow config > formatted name
  const workflowDisplayName = workflowData.recipeRequirements?.display_name || 
                               workflowData.workflow.displayName || 
                               formatTitle(workflowData.workflow.name);

  // some workflows (e.g. cortese-list) take their input from UI elements (address_list, etc.)
  // and declare no supported file types. for those we skip the uploader and render the form
  // directly so the user can reach the inputs and the submit button.
  const supportedFormats: string[] = workflowData.recipeRequirements?.supported_formats || [];
  const requiresFiles = supportedFormats.length > 0;

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          {workflowDisplayName}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {workflowDescription ?? 'Upload files to run this workflow'}
        </Typography>
        
        {requiresFiles && (
          <FileUploader 
            files={files} 
            setFiles={setFiles}
            showNextButton={false}
            acceptedFileTypes={workflowData.recipeRequirements?.supported_formats}
          />
        )}
        
        {(!requiresFiles || files.length > 0) && (
          <WorkflowForm 
            files={files} 
            onExecute={(executionArn) => {
              setFiles([]);
            }}
            preselectedPractice={workflowData.practice.id}
            preselectedWorkflows={[workflowData.workflow.id]}
          />
        )}
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {workflowDisplayName} Jobs
        </Typography>
        <UnifiedJobQueue 
          practice={workflowData.practice.id}
          workflow={workflowData.workflow.id}
          stepWorkflows={stepWorkflows}
          variant="full"
          features={{
            showFilters: true,
            showJsonEditor: true,
            showPdfViewer: true,
            showReviewForm: true,
            groupByStatus: false,
          }}
        />
      </Paper>
      
      <WorkflowHelp 
        practice={workflowData.practice.id}
        workflow={workflowData.workflow.id}
        helpText={workflowData.recipeRequirements?.help_text}
      />
    </Box>
  );
}